// A confirmation that something worked — RON-5465. Prototype region: `.toast`.
//
// WHY THE PORTAL HAS ITS OWN AND DOES NOT USE THE APP'S.
//
// Everywhere else in this product, `useApiUiResponse` raises the toast. **This module must not
// use it**, and the reason is not style: on response code 423 that helper calls
// `redirectToLogin()`, which clears `localStorage`, clears `sessionStorage` and navigates to
// `/login`.
//
// The portal is open to the internet and has no login. A contractor who hit that would be thrown
// to a page they cannot use, with their in-memory session destroyed and no way back except a new
// emailed code. It is the same reason `PortalAPI.jsx` does not import the shared `post()`: this
// page is outside the signed-in app, and anything that assumes otherwise is a trap.
//
// So this is deliberately small — a line of text that fades. It is not a notification system.
//
// WHAT IT IS FOR. Success only. Failures already have a place: the warning banner above the step
// cards, which stays on screen because a failure is something the contractor has to act on. A
// toast that disappears is the wrong container for a problem, and the right one for "that
// worked" — which, until now, the portal never said at all.

import { useEffect } from "react";

// Long enough to read a short sentence twice, short enough not to sit over the step cards. The
// prototype uses the same.
const DISMISS_MS = 4000;

const PortalToast = ({ message, onDismiss }) => {
  useEffect(() => {
    if (!message) return undefined;

    const timer = setTimeout(onDismiss, DISMISS_MS);

    // Cleared on every change, so a second toast arriving at 3.9 seconds gets its own full four
    // seconds rather than inheriting the tail of the first one's timer.
    return () => clearTimeout(timer);
  }, [message, onDismiss]);

  if (!message) return null;

  return (
    <div
      className="portal-toast"
      // `status` and polite, not `alert`: this interrupts nothing, and a contractor part-way
      // through reading a safety document should not have it read over them.
      role="status"
      aria-live="polite"
    >
      <span className="portal-toast__icon" aria-hidden="true">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
      {message}
    </div>
  );
};

export default PortalToast;
